/**
 * Submission + polling helpers for `/api/v1/bug-reports`.
 *
 * Backend contract (in flight, parallel specialist):
 *   POST /api/v1/bug-reports
 *     body: { source, title?, body, severity, module?, screenshot?, metadata }
 *     → 201 { id, status: 'pending' }
 *     → 429 { error: { code: 'RATE_LIMITED' } }
 *
 *   GET /api/v1/bug-reports/:id
 *     → 200 { id, githubIssueUrl?, githubIssueNumber?, status }
 *
 * The poll wraps the GET endpoint and resolves with the first response
 * that has a `githubIssueUrl`, or null after the timeout. Caller decides
 * whether to surface the URL or the "Submitted (URL pending)" copy.
 */

import { apiClient } from '@/lib/api-client';
import type { BugReportMetadata } from './bug-report-metadata';

export type BugSeverity = 'p0' | 'p1' | 'p2' | 'p3';

export interface BugReportSubmitInput {
  title?: string;
  body: string;
  severity: BugSeverity;
  module?: string;
  screenshot?: {
    base64: string;
    mimeType: 'image/png' | 'image/jpeg';
  };
  metadata: BugReportMetadata;
}

export interface BugReportSubmitResponse {
  id: string;
  status: 'pending' | string;
}

export interface BugReportStatusResponse {
  id: string;
  status: 'pending' | 'open' | 'failed' | string;
  githubIssueUrl?: string;
  githubIssueNumber?: number;
}

export async function submitBugReport(
  input: BugReportSubmitInput,
): Promise<BugReportSubmitResponse> {
  return apiClient.post<BugReportSubmitResponse>('/bug-reports', {
    source: 'user',
    title: input.title,
    body: input.body,
    severity: input.severity,
    module: input.module,
    screenshot: input.screenshot,
    metadata: input.metadata,
  });
}

/**
 * Poll for the GitHub URL. Resolves with the FIRST response that has
 * `githubIssueUrl` set, or null after `timeoutMs` without one.
 *
 * Polls every `intervalMs`. The default of 2s/30s matches the prompt.
 */
export async function pollForGithubUrl(
  id: string,
  opts: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<BugReportStatusResponse | null> {
  const interval = opts.intervalMs ?? 2_000;
  const timeout = opts.timeoutMs ?? 30_000;
  const deadline = Date.now() + timeout;

  while (Date.now() < deadline) {
    if (opts.signal?.aborted) return null;
    try {
      const res = await apiClient.get<BugReportStatusResponse>(
        `/bug-reports/${encodeURIComponent(id)}`,
      );
      if (res?.githubIssueUrl) return res;
    } catch {
      // Swallow — caller already has the submit response, the URL is
      // a "nice to have". A real failure surface comes from the
      // initial POST.
    }
    await sleep(interval, opts.signal);
  }
  return null;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    });
  });
}
